/*
 * fn: parseCardResponse
 *
 * Parses the Card API response to the Card object
 *
 */
import { type CardResponseType } from '@/defs/apiLegacy/cardResponse'
import { type CardPartComponentSlugType, type CardType, isCardPartComponentSlugType } from '@/defs/card'
import { isColorType, isRarityType } from '@/defs/mtg'
import { PRICE_SOURCES } from '@/defs/price'
import { getCardImage, getCardName, getCardRef, getColorId } from '@/utils/cardUtil'

const PARTNER_VARIANT_REGEX = /^Partner—([^(\n]+)/m

export const parseCardResponse = (card: CardResponseType): CardType => {
  const scryfallId = card.id
  const imageArt = getCardImage({ path: 'art', scryfallId })
  const image = card.image_uris?.[0] ? getCardImage({ path: 'grid', scryfallId }) : undefined
  const imageBack = card.image_uris?.[1] ? getCardImage({ face: 'back', path: 'grid', scryfallId }) : undefined
  const [nameFront, nameBack] = card.names?.length ? card.names : [card.name]
  const name = getCardName(nameFront, nameBack)
  const [oracleText, oracleTextBack] = card.card_faces?.length
    ? [card.card_faces[0]?.oracle_text, card.card_faces[1]?.oracle_text]
    : [card.oracle_text]
  const [typeline, typelineBack] = card.card_faces?.length
    ? [card.card_faces[0]?.type_line, card.card_faces[1]?.type_line]
    : [card.type_line]

  const cmcNumber = card.cmc !== undefined ? Number(card.cmc) : undefined
  const cmc = Number.isFinite(cmcNumber) ? cmcNumber : undefined

  const rarity = isRarityType(card.rarity) ? card.rarity : 'other'

  const partnerVariant =
    card.legal_partner && !card.partners_with
      ? oracleText?.match(PARTNER_VARIANT_REGEX)?.[1]?.trim().toLowerCase() || 'partner'
      : undefined

  return {
    allParts: card.all_parts
      ?.filter(
        (part): part is { component: CardPartComponentSlugType; name: string; slug?: string } =>
          !!part.name && isCardPartComponentSlugType(part.component),
      )
      .map(({ component, name, slug }) => ({ ...getCardRef({ name, slug }), component })),
    cmc,
    colorId: getColorId(card.color_identity),
    colors: card.color_identity?.map((c) => c.toLowerCase()).filter((c) => isColorType(c)),
    flavorCardRefs: card.flavor_refs
      ?.filter((c): c is { name: string; slug?: string } => !!c.name)
      .map((c) => getCardRef(c)),
    hasContentWarning: card.content_warning,
    image,
    imageArt,
    imageBack,
    isBackground: card.type?.includes('Background'),
    isBackgroundChooser: card.background_chooser,
    isBanned: card.banned,
    isDoctor: card.type?.includes('Doctor'),
    isDoctorsCompanion: card.doctors_companion,
    isGameChanger: card.game_changer,
    isLegalCommander: card.legal_commander,
    isLegalCompanion: card.legal_companion,
    isLegalPartner: card.legal_partner,
    isNew: card.new,
    isUniversesBeyond: card.universes_beyond,
    isUnofficial: card.unofficial,
    layout: card.layout,
    manaCost: card.mana_cost,
    name,
    nameBack,
    nameFront,
    oracleText,
    oracleTextBack,
    originalCardRef: card.oracle_ref?.name
      ? getCardRef({ name: card.oracle_ref.name, slug: card.oracle_ref.slug })
      : undefined,
    partnersWith: card.partners_with,
    partnerVariant,
    power: card.power,
    precon: card.precon ? card.precon : undefined,
    priceSources: PRICE_SOURCES.reduce<NonNullable<CardType['priceSources']>>((c, priceSource) => {
      const price = card.prices?.[priceSource]?.price
      const slug = card.prices?.[priceSource]?.slug
      if (price || slug) c[priceSource] = { price, slug }
      return c
    }, {}),
    primaryType: card.primary_type,
    rarity,
    releasedAt: card.released_at,
    salt: card.salt,
    scryfallId,
    scryfallUri: card.scryfall_uri,
    sets: card.sets,
    spellbookUri: card.spellbook_uri,
    spoiledAt: card.spoiled_at,
    tags: card.tags,
    toughness: card.toughness,
    type: card.type,
    typeline,
    typelineBack,
    types: card.types,
    uniqueArtwork: card.unique_artwork?.map((a) => ({
      artist: a.artist,
      collectorNumber: a.collector_number,
      image: a.image_uris?.[0],
      imageBack: a.image_uris?.[1],
      scryfallId: a.scryfall_id,
      set: a.set,
      setName: a.set_name,
    })),
  }
}
